import debug from 'debug';
import { useCallback, useEffect, useMemo } from 'react';
import { useInterval } from 'react-use';
import { useRecoilState, useRecoilValue } from 'recoil';
import { LOCAL_EVENT, SESSION_ID_CHECK_INTERVAL_MS, STORAGE_EVENT, } from '@/config/constants';
import { isStickySessionSelector, widgetIdSelector } from '@/state/config';
import { isLeaderSelector, sessionIdFromStorageState } from '@/state/session';
import { webChatStateState } from '@/state/socket';
import * as safeStorage from '@/util/safe-storage';
import { useEventListener } from './useEventListener';
/**
 * Manages the session ID with localStorage and sticky sessions.
 *
 * @param integrationId The integration id
 * @param withListeners Wether to add listeners to the window object for storage events
 */
export const useSessionId = (integrationId, withListeners = false) => {
    const sessionKey = `moveo_${integrationId}_session_id`;
    const [sessionIdFromStorage, setSessionIdFromStorage] = useRecoilState(sessionIdFromStorageState);
    const [webChatState, setWebChatState] = useRecoilState(webChatStateState);
    const isLeader = useRecoilValue(isLeaderSelector);
    const widgetId = useRecoilValue(widgetIdSelector);
    const isStickySessions = useRecoilValue(isStickySessionSelector);
    const refreshSessionFromStorage = useCallback(() => {
        if (!isStickySessions) {
            return;
        }
        setSessionIdFromStorage(safeStorage.getItem(sessionKey));
    }, [isStickySessions, sessionKey, setSessionIdFromStorage]);
    const log = useMemo(() => debug(`moveo:iframe:${widgetId}:session-id`), [widgetId]);
    useInterval(refreshSessionFromStorage, SESSION_ID_CHECK_INTERVAL_MS);
    useEffect(() => {
        refreshSessionFromStorage();
    }, [refreshSessionFromStorage]);
    const setSessionId = useCallback((id) => {
        // Sets the id here before calling `handleStorageChange()` so that
        // listeners already have the new value before setting the new web chat state
        setSessionIdFromStorage(id);
        // With sticky sessions we need to store the session id in local storage
        if (id && isStickySessions) {
            safeStorage.setItem(sessionKey, id);
        }
        else {
            safeStorage.removeItem(sessionKey);
            // We dispatch a custom event so the caller is also notified
            // When removing a session id.
            window.dispatchEvent(new StorageEvent(LOCAL_EVENT, {
                key: sessionKey,
                url: 'local',
            }));
        }
    }, [isStickySessions, sessionKey, setSessionIdFromStorage]);
    const handleStorageChange = useCallback((event) => {
        const e = event;
        const newSessionId = e.newValue;
        // Ignore storage events if sticky sessions is disabled
        // Ignore storage events if listeners are disabled
        // Ignore events for other keys
        if (!isStickySessions || !withListeners || e.key !== sessionKey) {
            return;
        }
        if (newSessionId === sessionIdFromStorage) {
            log('handleStorageChange: Ignore event, sessionId did not changed');
            return;
        }
        log(`handleStorageChange: webChatState: ${webChatState}, newValue: ${newSessionId}, isLeader: ${isLeader}`, e);
        // Do nothing if we are creating a session
        if (webChatState === "creating" /* WebChatState.Creating */) {
            log('handleStorageChange: Ignore create session, a session is being created already');
            return;
        }
        setSessionIdFromStorage(newSessionId);
        // If there is a session id, reconnect
        if (newSessionId) {
            setWebChatState("reconnect" /* WebChatState.Reconnect */);
            return;
        }
        // If there is not session id and this is the leader, create a session
        if (!newSessionId && isLeader && webChatState === "idle" /* WebChatState.Idle */) {
            log('handleStorageChange: No new session and this is the leader, create session');
            setWebChatState("create" /* WebChatState.Create */);
            return;
        }
        else {
            log('handleStorageChange: No new session and this is not the leader, do nothing');
        }
    }, [
        log,
        isLeader,
        sessionKey,
        webChatState,
        withListeners,
        isStickySessions,
        sessionIdFromStorage,
        setSessionIdFromStorage,
        setWebChatState,
    ]);
    useEventListener(STORAGE_EVENT, handleStorageChange);
    useEventListener(LOCAL_EVENT, handleStorageChange);
    useEffect(() => {
        if (widgetId && withListeners) {
            log(`stickySession: ${isStickySessions}`);
        }
    }, [log, widgetId, isStickySessions, withListeners]);
    useEffect(() => {
        if (widgetId && withListeners) {
            log(`sessionId: ${sessionIdFromStorage}`);
        }
    }, [log, sessionIdFromStorage, widgetId, withListeners]);
    return setSessionId;
};
